import { useEffect } from 'react';

import { useFormContext } from 'react-hook-form';

import { inputFontOptions } from 'views/Builder/constants';
import { z } from 'zod';

import AcGradientInput from 'components/AcGradientInput/AcGradientInput';
import InputSelectDropdown from 'components/InputSelectDropdown/InputSelectDropdown';
import { FormControl, FormField, FormItem, FormMessage } from 'components/ui';
import { ColorPicker } from 'components/ui/ColorPicker/color-picker';
import UploadImage from 'components/UploadImage/UploadImage';

import { createColorSetValueHandler } from '../utils/blockForms.utils';
import { templateStudioSchema } from '../validation-schema';

export const BrandKitForm: React.FC = () => {
  const {
    control,
    setValue,
    watch,
    formState: { errors, touchedFields }
  } = useFormContext<z.infer<typeof templateStudioSchema>>();

  const footerDesktopImage = watch('brandKit.footerDesktopImage');

  useEffect(() => {
    setValue('brandKit.footerMobileImage', footerDesktopImage);
  }, [footerDesktopImage]);

  return (
    <div className="flex flex-col gap-5 text-md text-base-foreground mt-6 mb-2">
      <FormField
        control={control}
        name="brandKit.fontFamily"
        render={({ field: { onChange } }) => (
          <FormItem>
            <div className="flex flex-col items-start gap-2">
              <label className="font-medium text-base-foreground text-sm">
                Font Family
              </label>
              <FormControl>
                <InputSelectDropdown
                  data-testid="BrandKit-FontFamily"
                  buttonDataTestId="BrandKit-FontFamilySelect"
                  listDataTestId="BrandKit-FontFamilySelectList"
                  defaultSelected={inputFontOptions.find(
                    (option) => option.value === watch('brandKit.fontFamily')
                  )}
                  options={inputFontOptions}
                  onOptionChange={onChange}
                  placeholder={'Select a font'}
                />
              </FormControl>
              <FormMessage />
            </div>
          </FormItem>
        )}
      />
      <FormField
        name="brandKit.buttonColor"
        control={control}
        render={({ field: { value, onChange } }) => (
          <FormItem>
            <div className="flex flex-col items-start gap-2">
              <label className="font-medium text-base-foreground text-sm">
                Button Color
              </label>
              <FormControl className="w-full">
                <div className="[&_.MuiInputAdornment-root]:mr-0 [&_.MuiTextField-root]:!w-[100%] [&_.MuiInputBase-input]:!pl-0">
                  <AcGradientInput
                    key={`brandKit-buttonColor-${JSON.stringify(value)}`}
                    name="brandKit.buttonColor"
                    defaultValue={
                      value || {
                        colorOne: '#CB1010',
                        colorTwo: '#FF0000'
                      }
                    }
                    setValue={createColorSetValueHandler(
                      'brandKit.buttonColor',
                      () => watch('brandKit.buttonColor'),
                      setValue as (name: string, value: any) => void,
                      onChange
                    )}
                    onlySolid={false}
                    circular={true}
                    imgWidth="16px"
                    imgHeight="16px"
                    error={
                      touchedFields?.brandKit?.buttonColor &&
                      Boolean(errors.brandKit?.buttonColor)
                    }
                    helperText={
                      touchedFields?.brandKit?.buttonColor
                        ? errors.brandKit?.buttonColor?.toString()
                        : ''
                    }
                  />
                </div>
              </FormControl>
            </div>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        name="brandKit.buttonTextColor"
        control={control}
        render={({ field: { value, onChange } }) => (
          <FormItem>
            <FormControl>
              <ColorPicker
                label="Button Text Color"
                data-testid="BrandKit-ButtonTextColor"
                onChange={onChange}
                defaultValue={value}
              />
            </FormControl>
          </FormItem>
        )}
      />
      <FormField
        name="brandKit.logo"
        control={control}
        render={({ field: { value, onChange } }) => (
          <FormItem>
            <FormControl>
              <div data-testid="BrandKit-LogoUpload">
                <UploadImage
                  isRequired
                  className="w-full !flex flex-row px-5"
                  title="Logo"
                  buttonText="Upload"
                  data-testid="BrandKit-LogoUpload"
                  selectedImageUrl={value}
                  onUpload={(url: string) => {
                    onChange(url);
                  }}
                  labelClassName="text-xs"
                  TooltipContent={() => (
                    <div>
                      <p>Dimensions: 1:1 Ratio (Square)</p>
                      <p>File Type: PNG or JPEG</p>
                      <p>Minimum Width: 320px</p>
                    </div>
                  )}
                />
              </div>
            </FormControl>
            <FormMessage className="mt-1 !mb-5 text-p-s text-base-destructive" />
          </FormItem>
        )}
      />
      <FormField
        name="brandKit.defaultBanner"
        control={control}
        render={({ field: { value, onChange } }) => (
          <FormItem>
            <FormControl>
              <div data-testid="BrandKit-DefaultBannerUpload">
                <UploadImage
                  className="w-full !flex flex-row px-5"
                  title="Banner Image"
                  buttonText="Upload"
                  data-testid="BrandKit-DefaultBannerUpload"
                  selectedImageUrl={value}
                  onUpload={(url: string) => {
                    onChange(url);
                  }}
                  labelClassName="text-xs"
                  TooltipContent={() => (
                    <div>
                      <p>Dimensions: 4:1/3:1 Ratio (Horizontal)</p>
                      <p>File Type: PNG or JPEG</p>
                      <p>Minimum Width: 400px</p>
                    </div>
                  )}
                />
              </div>
            </FormControl>
            <FormMessage className="mt-1 !mb-5 text-p-s text-base-destructive" />
          </FormItem>
        )}
      />
      <FormField
        name="brandKit.backgroundImageMobile"
        control={control}
        render={({ field: { value, onChange } }) => (
          <FormItem>
            <FormControl>
              <div data-testid="BrandKit-BackgroundImageMobileUpload">
                <UploadImage
                  isRequired
                  className="w-full !flex flex-row px-5"
                  title="Background Image (Mobile)"
                  buttonText="Upload"
                  data-testid="BrandKit-BackgroundImageMobileUpload"
                  selectedImageUrl={value}
                  onUpload={(url: string) => {
                    onChange(url);
                  }}
                  labelClassName="text-xs"
                  TooltipContent={() => (
                    <div>
                      <p>Dimensions: 1:2 Ratio (Vertical)</p>
                      <p>File Type: PNG or JPEG</p>
                      <p>Minimum Width: 400px</p>
                    </div>
                  )}
                />
              </div>
            </FormControl>
            <FormMessage className="mt-1 !mb-5 text-p-s text-base-destructive" />
          </FormItem>
        )}
      />
      <FormField
        name="brandKit.backgroundImageDesktop"
        control={control}
        render={({ field: { value, onChange } }) => (
          <FormItem>
            <FormControl>
              <div data-testid="BrandKit-BackgroundImageDesktopUpload">
                <UploadImage
                  isRequired
                  className="w-full !flex flex-row px-5"
                  title="Background Image (Desktop)"
                  buttonText="Upload"
                  data-testid="BrandKit-BackgroundImageDesktopUpload"
                  selectedImageUrl={value}
                  onUpload={(url: string) => {
                    onChange(url);
                  }}
                  labelClassName="text-xs"
                  TooltipContent={() => (
                    <div>
                      <p>Dimensions: 1920px width x 1080px hight</p>
                      <p>File Type: PNG or JPEG</p>
                    </div>
                  )}
                />
              </div>
            </FormControl>
            <FormMessage className="mt-1 !mb-5 text-p-s text-base-destructive" />
          </FormItem>
        )}
      />
      <FormField
        name="brandKit.footerDesktopImage"
        control={control}
        render={({ field: { value, onChange } }) => (
          <FormItem>
            <FormControl>
              <div data-testid="BrandKit-FooterDesktopImageUpload">
                <UploadImage
                  className="w-full !flex flex-row px-5"
                  title="Footer Image"
                  buttonText="Upload"
                  data-testid="BrandKit-FooterDesktopImageUpload"
                  selectedImageUrl={value}
                  onUpload={(url: string) => {
                    onChange(url);
                  }}
                  labelClassName="text-xs"
                  TooltipContent={() => (
                    <div>
                      <p>Dimensions: 2:1 (Horizontal)</p>
                      <p>File Type: PNG or JPEG</p>
                      <p>Minimum Width: 400px</p>
                    </div>
                  )}
                />
              </div>
            </FormControl>
            <FormMessage className="mt-1 !mb-5 text-p-s text-base-destructive" />
          </FormItem>
        )}
      />
    </div>
  );
};
